import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight, Heart, Clock, TrendingUp, Sparkles, MessageCircle, Zap, BookOpen, ExternalLink, ChevronRight, Newspaper, Scale, FileText } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { SEOHead } from '@/components/SEOHead';
import { useLanguage } from '@/contexts/LanguageContext';
import { NewsletterForm } from '@/components/NewsletterForm';

type Category = 'all' | 'diy' | 'ai-news' | 'strategic';

interface Article {
  slug: string;
  title: string;
  excerpt: string;
  date: string;
  category: Category;
  categoryLabel: string;
  readTime: string;
  isFavorite?: boolean;
  image?: string;
}

// ── Category color helpers ──────────────────────────────────────────
const categoryStyles: Record<Category, { badge: string; accent: string; color: string }> = {
  diy: { badge: 'bg-emerald-50 text-emerald-700 border-emerald-200', accent: 'from-emerald-500/10 to-emerald-600/5', color: 'text-emerald-600' },
  'ai-news': { badge: 'bg-blue-50 text-blue-700 border-blue-200', accent: 'from-blue-500/10 to-blue-600/5', color: 'text-blue-600' },
  strategic: { badge: 'bg-purple-50 text-purple-700 border-purple-200', accent: 'from-purple-500/10 to-purple-600/5', color: 'text-purple-600' },
  all: { badge: 'bg-secondary text-secondary-foreground border-border', accent: '', color: 'text-foreground' },
};

const categoryImages: Record<Category, string> = {
  diy: '/images/blog-card-ai-tools.webp',
  'ai-news': '/images/blog-card-news.webp',
  strategic: '/images/blog-card-strategy.webp',
  all: '/images/blog-hero-lawyers-ai.webp',
};

// ── Category Badge ──────────────────────────────────────────────────
const CatBadge = ({ category, label }: { category: Category; label: string }) => (
  <span className={`inline-flex items-center text-xs font-medium px-2.5 py-0.5 rounded-full border ${categoryStyles[category].badge}`}>
    {label}
  </span>
);

// ── Hero "À la une" (rotating) ──────────────────────────────────────
const HeroUne = ({ articles, language }: { articles: Article[]; language: string }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goToSlide = useCallback((index: number) => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIndex(index);
      setIsTransitioning(false);
    }, 300);
  }, []);

  useEffect(() => {
    if (articles.length <= 1) return;
    const interval = setInterval(() => {
      goToSlide((currentIndex + 1) % articles.length);
    }, 7000);
    return () => clearInterval(interval);
  }, [currentIndex, articles.length, goToSlide]);

  const article = articles[currentIndex];
  if (!article) return null;

  return (
    <div className="relative">
      <Link
        to={`/articles/${article.slug}`}
        className="group block overflow-hidden rounded-2xl bg-gradient-to-br from-primary via-primary/80 to-accent border border-primary/30 hover:border-accent/50 transition-all duration-300 shadow-xl"
      >
        <div className={`p-8 md:p-12 lg:p-14 transition-opacity duration-300 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>
          <div className="flex items-center gap-3 mb-4">
            <Badge className="bg-white/20 text-white border-white/30 shadow-lg">
              <Sparkles className="w-3 h-3 mr-1" />
              {language === 'fr' ? 'À la une' : 'Featured'}
            </Badge>
            <span className="inline-flex items-center text-xs font-medium px-2.5 py-0.5 rounded-full border bg-white/20 text-white border-white/30">
              {article.categoryLabel}
            </span>
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white leading-tight mb-4 max-w-3xl group-hover:text-white/90 transition-colors">
            {article.title}
          </h2>
          <p className="text-white/80 text-base md:text-lg leading-relaxed mb-5 max-w-2xl">
            {article.excerpt}
          </p>
          <div className="flex items-center gap-4 text-sm text-white/70">
            <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" />{article.date}</span>
            <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" />{article.readTime}</span>
            <span className="inline-flex items-center gap-1.5 text-white font-medium ml-auto">
              {language === 'fr' ? 'Lire l\'article' : 'Read article'} <ArrowRight className="w-4 h-4" />
            </span>
          </div>
        </div>
      </Link>
      {articles.length > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          {articles.map((_, i) => (
            <button
              key={i}
              onClick={() => goToSlide(i)}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                i === currentIndex ? 'bg-primary w-6' : 'bg-border hover:bg-muted-foreground/40'
              }`}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ── Section Header (LMD-style) ──────────────────────────────────────
const SectionHeader = ({ icon: Icon, title, color = 'text-primary' }: { icon: React.ElementType; title: string; color?: string }) => (
  <div className="flex items-center gap-3 mb-6">
    <div className={`w-1 h-8 rounded-full bg-primary`} />
    <Icon className={`w-5 h-5 ${color}`} />
    <h2 className="text-xl font-bold text-foreground uppercase tracking-wide">{title}</h2>
  </div>
);

// ── Article Row (editorial style, no image) ─────────────────────────
const ArticleRow = ({ article, language, showBorder = true }: { article: Article; language: string; showBorder?: boolean }) => (
  <Link
    to={`/articles/${article.slug}`}
    className={`group flex items-start gap-4 py-4 ${showBorder ? 'border-b border-border' : ''} hover:bg-secondary/30 transition-colors -mx-2 px-2 rounded-lg`}
  >
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 mb-1.5">
        <CatBadge category={article.category} label={article.categoryLabel} />
        {article.isFavorite && (
          <span className="inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full bg-destructive/10 text-destructive border border-destructive/20">
            <Heart className="w-3 h-3 mr-1 fill-current" />
            {language === 'fr' ? 'Coup de cœur' : 'Our Pick'}
          </span>
        )}
      </div>
      <h3 className="text-base font-semibold text-foreground leading-snug group-hover:text-primary transition-colors line-clamp-2 mb-1">
        {article.title}
      </h3>
      <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2 mb-1.5">
        {article.excerpt}
      </p>
      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{article.date}</span>
        <span>{article.readTime}</span>
      </div>
    </div>
    <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0 mt-4 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
  </Link>
);

// ── Image Card ──────────────────────────────────────────────────────
const ImageCard = ({ article, language, size = 'md' }: { article: Article; language: string; size?: 'lg' | 'md' }) => {
  const img = article.image || categoryImages[article.category];
  return (
    <Link
      to={`/articles/${article.slug}`}
      className="group flex flex-col h-full rounded-xl border border-border bg-card overflow-hidden hover:shadow-xl hover:border-primary/20 transition-all duration-300"
    >
      <div className={`relative overflow-hidden ${size === 'lg' ? 'aspect-[16/9]' : 'aspect-[3/2]'}`}>
        <img src={img} alt={article.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
        <div className="absolute top-3 left-3 flex items-center gap-2">
          <CatBadge category={article.category} label={article.categoryLabel} />
        </div>
      </div>
      <div className="flex-1 p-5">
        <h3 className={`font-semibold text-foreground leading-snug mb-2 group-hover:text-primary transition-colors line-clamp-2 ${size === 'lg' ? 'text-xl' : 'text-base'}`}>
          {article.title}
        </h3>
        <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2 mb-3">{article.excerpt}</p>
      </div>
      <div className="px-5 pb-4 mt-auto">
        <div className="flex items-center justify-between text-xs text-muted-foreground border-t border-border pt-3">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{article.date}</span>
            <span>{article.readTime}</span>
          </div>
          <ArrowRight className="w-4 h-4 text-primary opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
        </div>
      </div>
    </Link>
  );
};

// ── Trending Sidebar ────────────────────────────────────────────────
const TrendingSection = ({ articles, language }: { articles: Article[]; language: string }) => (
  <div className="rounded-xl border border-border bg-card p-5">
    <div className="flex items-center gap-2 mb-4">
      <Zap className="w-5 h-5 text-accent" />
      <h3 className="font-bold text-foreground">{language === 'fr' ? 'Les plus lus' : 'Trending'}</h3>
    </div>
    <div className="space-y-0 divide-y divide-border">
      {articles.map((article, i) => (
        <Link key={i} to={`/articles/${article.slug}`} className="group flex items-start gap-3 py-3 first:pt-0 last:pb-0">
          <span className="shrink-0 w-7 h-7 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">{i + 1}</span>
          <div className="min-w-0">
            <h4 className="text-sm font-medium text-foreground leading-snug group-hover:text-primary transition-colors line-clamp-2">{article.title}</h4>
            <span className="text-xs text-muted-foreground mt-0.5 block">{article.readTime}</span>
          </div>
        </Link>
      ))}
    </div>
  </div>
);

// ── Reader Engagement ───────────────────────────────────────────────
const ReaderQuestion = ({ language }: { language: string }) => (
  <div className="rounded-2xl border-2 border-dashed border-primary/20 bg-gradient-to-br from-primary/5 to-accent/5 p-6 md:p-8">
    <div className="flex items-start gap-4">
      <div className="shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
        <MessageCircle className="w-6 h-6 text-primary" />
      </div>
      <div>
        <h3 className="text-lg font-bold text-foreground mb-2">
          {language === 'fr' ? 'Et vous, où en êtes-vous ?' : 'Where do you stand?'}
        </h3>
        <p className="text-muted-foreground leading-relaxed mb-4">
          {language === 'fr'
            ? 'Votre cabinet ou direction juridique utilise-t-il déjà des outils d\'IA au quotidien ? Avez-vous formé vos équipes ? Nous accompagnons les professionnels du droit dans leur montée en compétences — de la découverte aux cas d\'usage avancés.'
            : 'Is your firm or legal department already using AI tools daily? Have you trained your teams? We help legal professionals upskill — from discovery to advanced use cases.'}
        </p>
        <Link to={language === 'fr' ? '/formations' : '/training'} className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline">
          {language === 'fr' ? 'Découvrir nos formations' : 'Explore our training'} <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════════════
// ── MAIN PAGE ─────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════
export const BlogPage = () => {
  const { t, language } = useLanguage();
  const [activeCategory, setActiveCategory] = useState<Category>('all');

  const categories = language === 'fr' ? [
    { id: 'all' as Category, label: 'Tous', icon: BookOpen },
    { id: 'diy' as Category, label: 'Do It Yourself', icon: Zap },
    { id: 'ai-news' as Category, label: 'Legal AI News', icon: TrendingUp },
    { id: 'strategic' as Category, label: 'Analyse Stratégique', icon: Sparkles },
  ] : [
    { id: 'all' as Category, label: 'All', icon: BookOpen },
    { id: 'diy' as Category, label: 'Do It Yourself', icon: Zap },
    { id: 'ai-news' as Category, label: 'Legal AI News', icon: TrendingUp },
    { id: 'strategic' as Category, label: 'Strategic Analysis', icon: Sparkles },
  ];

  // ── Articles data (FR) ────────────────────────────────────────────
  const articles: Article[] = language === 'fr' ? [
    {
      slug: 'guide-cnil-has-ia-sante-professions-reglementees',
      title: 'Guide CNIL-HAS sur l\'IA en santé : l\'exigence maximale qui annonce l\'avenir des professions réglementées',
      excerpt: 'Analyse complète du guide HAS-CNIL. 12 fiches, 3 niveaux d\'exigence et un parallèle saisissant avec le secret professionnel de l\'avocat.',
      date: '12 mars 2026',
      category: 'strategic',
      categoryLabel: 'Analyse Stratégique',
      readTime: '18 min',
    },
    {
      slug: 'code-pratique-ia-commission-europeenne-marquage-2026',
      title: 'Commission européenne : le Code de Pratique sur le marquage du contenu IA qui transforme les obligations des professions réglementées',
      excerpt: 'Analyse du 2e projet de Code de Pratique (mars 2026). Obligations fournisseurs/déployeurs, icône AI standardisée, exception éditoriale pour les avocats.',
      date: '12 mars 2026',
      category: 'strategic',
      categoryLabel: 'Analyse Stratégique',
      readTime: '16 min',
    },
    {
      slug: 'legal-ops-metier-ia-juridique',
      title: 'LegalOps : 5 choses à comprendre sur ce métier en devenir',
      excerpt: 'Origines, missions, formations AFJE, salaires 35-70k€ et rôle catalyseur de l\'AI LegalOps dans les directions juridiques.',
      date: '5 mars 2026',
      category: 'strategic',
      categoryLabel: 'Analyse Stratégique',
      readTime: '15 min',
      image: '/images/legal-ops-metier-ia-juridique-guide-complet.webp',
    },
    {
      slug: 'automatisation-cabinet-avocat-ia',
      title: 'Automatisation d\'un cabinet d\'avocat : 3 processus à transformer dès aujourd\'hui',
      excerpt: '40 % de votre charge administrative éliminée. Budget : 45 €/mois. Guide pas à pas.',
      date: '4 mars 2026',
      category: 'diy',
      categoryLabel: 'Do It Yourself',
      readTime: '12 min',
      image: '/images/automatisation-cabinet-avocat-ia-guide-complet.webp',
    },
    {
      slug: 'tribune-ia-generative-inflation-normative',
      title: '[Tribune] L\'IA générative juridique, alliée des juristes face à l\'inflation normative ?',
      excerpt: 'Tribune publiée sur le Village de la Justice. 92 424 articles législatifs, 248 343 réglementaires : quand l\'IA devient l\'outil indispensable.',
      date: '2 mars 2026',
      category: 'strategic',
      categoryLabel: 'Analyse Stratégique',
      readTime: '8 min',
      image: '/images/seo-about-legal-ai-expertise.webp',
    },
    {
      slug: 'hallucinations-ia-juridictions-francaises',
      title: 'Les hallucinations d\'IA devant les juridictions françaises : premiers cas et implications déontologiques',
      excerpt: 'TA Grenoble, TA Orléans, décembre 2025 : analyse des premières décisions identifiant des jurisprudences fictives générées par IA et des obligations déontologiques des avocats.',
      date: '26 février 2026',
      category: 'strategic',
      categoryLabel: 'Analyse Stratégique',
      readTime: '18 min',
      image: '/images/seo-audit-strategie-ia-juridique.webp',
    },
    {
      slug: 'agents-ia-avocats-directions-juridiques',
      title: 'Agents IA : ce que tout avocat et directeur juridique doit comprendre en 2026',
      excerpt: 'Définition pédagogique, différence avec les chatbots, et neuf cas d\'usage concrets pour les cabinets et les directions juridiques.',
      date: '24 février 2026',
      category: 'strategic',
      categoryLabel: 'Analyse Stratégique',
      readTime: '22 min',
      image: '/images/seo-about-legal-ai-expertise.webp',
    },
    {
      slug: 'formation-obligatoire-avocat-fco',
      title: 'Formation obligatoire avocat : guide complet de la FCO et place de l\'IA',
      excerpt: '20 heures par an, obligations renforcées pour les jeunes avocats, e-learning, barreau de Paris, CNB : tout savoir sur la formation continue obligatoire.',
      date: '24 février 2026',
      category: 'strategic',
      categoryLabel: 'Analyse Stratégique',
      readTime: '18 min',
    },
    {
      slug: 'jimini-ai-gide-partenariat',
      title: 'Jimini AI × Gide Loyrette Nouel : le partenariat qui marque la legal tech française',
      excerpt: 'Le premier cabinet français d\'affaires choisit Jimini AI. Verbatims, analyse et enjeux de souveraineté.',
      date: '18 février 2026',
      category: 'ai-news',
      categoryLabel: 'AI News',
      readTime: '8 min',
      image: '/images/seo-blog-actualites-ia-juridique.webp',
    },
    {
      slug: 'barreaux-conventions-legal-tech',
      title: 'Tour d\'horizon des barreaux français : toutes les conventions signées avec la legal tech IA',
      excerpt: 'De Paris à Strasbourg, cartographie complète des partenariats entre barreaux et acteurs de l\'IA juridique.',
      date: '12 février 2026',
      category: 'strategic',
      categoryLabel: 'Analyse Stratégique',
      readTime: '15 min',
    },
    {
      slug: 'bilan-hoffman-batonnier-ia',
      title: 'Bilan du bâtonnier Pierre Hoffman : l\'IA en héritage au barreau de Paris',
      excerpt: '5 conventions legal tech, 3 000 avocats formés, un Comité Innovation pérenne : retour sur deux ans de transformation numérique.',
      date: '5 février 2026',
      category: 'strategic',
      categoryLabel: 'Analyse Stratégique',
      readTime: '14 min',
    },
    {
      slug: 'budget-ia-entreprises-cabinets-avocats',
      title: '98 % des entreprises augmentent leur budget IA en 2026 : et les cabinets d\'avocats ?',
      excerpt: 'Étude ABBYY 2025 : 98 % des entreprises françaises augmentent leur budget IA. Analyse du décalage avec les cabinets d\'avocats.',
      date: '29 janvier 2026',
      category: 'strategic',
      categoryLabel: 'Analyse Stratégique',
      readTime: '16 min',
    },
    {
      slug: 'budget-ia-gouvernement-france-2026',
      title: 'Budget IA en France 2026 : sanctuarisé, préservé ou menacé ?',
      excerpt: 'Coupes France 2030, plan Osez l\'IA, rapport de la Cour des comptes : décryptage des orientations gouvernementales.',
      date: '23 janvier 2026',
      category: 'strategic',
      categoryLabel: 'Analyse Stratégique',
      readTime: '18 min',
    },
    {
      slug: 'combien-outils-ia-avocat',
      title: 'Combien d\'outils IA faut-il maîtriser pour être vraiment efficace ?',
      excerpt: 'Harvey, ChatGPT, Claude, Doctrine, Jimini AI… La multiplication des outils impose une formation pluridisciplinaire.',
      date: '16 janvier 2026',
      category: 'strategic',
      categoryLabel: 'Analyse Stratégique',
      readTime: '18 min',
    },
    {
      slug: 'guide-francenum-ia-generative-avocats',
      title: 'Guide France Num sur l\'IA générative pour les avocats : analyse critique',
      excerpt: 'Décryptage du guide pratique francenum.gouv.fr / CNB, ses forces, ses limites, et positionnement international.',
      date: '9 janvier 2026',
      category: 'strategic',
      categoryLabel: 'Analyse Stratégique',
      readTime: '15 min',
    },
    {
      slug: 'formation-ia-avocat-lamy-liaisons-analyse',
      title: 'Formation IA avocat : analyse complète de l\'offre Lamy Liaisons Académie 2026',
      excerpt: 'Programme détaillé, modules, prix, forces et limites de la formation « Droit de l\'intelligence artificielle ».',
      date: '2 janvier 2026',
      category: 'strategic',
      categoryLabel: 'Analyse Stratégique',
      readTime: '18 min',
    },
    {
      slug: 'genial-dalloz-formation-ia-avocat-analyse',
      title: 'GenIA-L et formations IA Lefebvre Dalloz : analyse complète',
      excerpt: 'Revue détaillée de l\'IA juridique GenIA-L et du catalogue de formations IA de Lefebvre Dalloz Compétences.',
      date: '19 décembre 2025',
      category: 'strategic',
      categoryLabel: 'Analyse Stratégique',
      readTime: '20 min',
    },
    {
      slug: 'webinaire-vs-bootcamp-formation-ia',
      title: 'Webinaire ou bootcamp IA : quel format de formation choisir ?',
      excerpt: 'Comparaison détaillée entre webinaires ponctuels et bootcamps immersifs pour structurer la montée en compétences IA.',
      date: '12 décembre 2025',
      category: 'strategic',
      categoryLabel: 'Analyse Stratégique',
      readTime: '16 min',
    },
    {
      slug: 'formation-ia-juristes-entreprise',
      title: 'Formation IA pour juristes d\'entreprise : enjeux du secret professionnel',
      excerpt: 'Avec le nouveau legal privilege, les juristes d\'entreprise doivent maîtriser l\'IA sans compromettre la confidentialité.',
      date: '5 décembre 2025',
      category: 'strategic',
      categoryLabel: 'Analyse Stratégique',
      readTime: '18 min',
    },
    {
      slug: 'transfo-droit-2025-recapitulatif-salon-ia-juridique',
      title: 'TransfoDroit 2025 : récapitulatif complet du plus grand salon de l\'IA juridique en Europe',
      excerpt: '4 800 participants, 80 exposants, 120+ conférences : retour détaillé et sourcé sur les deux journées du salon des Transformations du Droit.',
      date: '2 décembre 2025',
      category: 'strategic',
      categoryLabel: 'Analyse Stratégique',
      readTime: '22 min',
    },
    {
      slug: 'financement-formation-ia-juridique',
      title: 'Comment financer votre formation IA juridique',
      excerpt: 'CPF, formation continue obligatoire, OPCO, FNE : guide complet des mécanismes de financement.',
      date: '28 novembre 2025',
      category: 'diy',
      categoryLabel: 'Do It Yourself',
      readTime: '14 min',
      image: '/images/seo-training-formation-ia-avocat.webp',
    },
    {
      slug: 'comparatif-formations-ia-avocats',
      title: 'Top 5 des formations IA pour avocats en 2026 : le comparatif indépendant',
      excerpt: 'Lamy, LexisNexis, EFB, Avocat 2.0, Gaius : quelle formation choisir ? Analyse détaillée.',
      date: '21 novembre 2025',
      category: 'strategic',
      categoryLabel: 'Analyse Stratégique',
      readTime: '20 min',
    },
    {
      slug: 'guide-cnb-ia-generative-avocats',
      title: 'Guide du CNB sur l\'IA générative : les recommandations aux avocats',
      excerpt: 'Décryptage complet du premier guide officiel de la profession sur l\'utilisation de l\'IA générative.',
      date: '14 novembre 2025',
      category: 'strategic',
      categoryLabel: 'Analyse Stratégique',
      readTime: '18 min',
    },
    {
      slug: 'claude-cowork-juridique',
      title: 'Claude Cowork Legal : le plugin qui a fait trembler les marchés',
      excerpt: '285 milliards de dollars évaporés. Thomson Reuters -16%. Le buzz était-il justifié ?',
      date: '7 novembre 2025',
      category: 'ai-news',
      categoryLabel: 'AI News',
      readTime: '22 min',
      image: '/images/seo-home-legal-ai-training.webp',
    },
    {
      slug: 'test-claude-legal-plugin',
      title: 'Claude Legal Plugin 2026 : test complet',
      excerpt: '3 semaines de tests intensifs. Le verdict sans filtre sur le plugin qui a fait trembler Wall Street.',
      date: '31 octobre 2025',
      category: 'ai-news',
      categoryLabel: 'AI News',
      readTime: '20 min',
    },
    {
      slug: 'checklist-rgpd-ia-juridique',
      title: 'Les 12 points de vigilance RGPD avant d\'adopter une IA juridique',
      excerpt: 'Checklist RGPD et déontologique en 12 points. Hébergement, chiffrement, certifications, AI Act.',
      date: '24 octobre 2025',
      category: 'diy',
      categoryLabel: 'Do It Yourself',
      readTime: '15 min',
    },
    {
      slug: 'guide-ultime-ia-juridique',
      title: 'Le guide ultime de l\'IA juridique pour avocats en 2026',
      excerpt: 'De ChatGPT à Harvey AI : le guide complet pour intégrer l\'IA dans votre pratique juridique.',
      date: '17 octobre 2025',
      category: 'diy',
      categoryLabel: 'Do It Yourself',
      readTime: '25 min',
      image: '/images/seo-contact-formation-ia-cabinet.webp',
    },
    {
      slug: 'choisir-ia-juridique-5-etapes',
      title: 'Comment choisir votre IA juridique en 5 étapes',
      excerpt: 'Le framework décisionnel en 5 étapes pour sélectionner l\'outil IA adapté à votre pratique.',
      date: '10 octobre 2025',
      category: 'diy',
      categoryLabel: 'Do It Yourself',
      readTime: '18 min',
    },
    {
      slug: 'harvey-doctrine-claude-comparatif',
      title: 'Harvey AI vs Doctrine vs Claude Legal : le comparatif 2026',
      excerpt: 'Comparatif détaillé des 3 outils IA juridiques dominants. Tests, tarifs, conformité RGPD.',
      date: '3 octobre 2025',
      category: 'ai-news',
      categoryLabel: 'AI News',
      readTime: '22 min',
    },
    {
      slug: '7-erreurs-deploiement-ia',
      title: 'Les 7 erreurs fatales du déploiement IA en cabinet',
      excerpt: '87% des projets IA échouent en cabinet d\'avocats. Voici les 7 erreurs qui les tuent.',
      date: '26 septembre 2025',
      category: 'strategic',
      categoryLabel: 'Analyse Stratégique',
      readTime: '15 min',
    },
    {
      slug: 'bonnes-pratiques-ia-cabinet',
      title: 'Les 10 bonnes pratiques pour intégrer l\'IA en cabinet',
      excerpt: 'Le playbook complet issu de 50+ déploiements réussis. Ce qui marche vraiment.',
      date: '19 septembre 2025',
      category: 'diy',
      categoryLabel: 'Do It Yourself',
      readTime: '18 min',
    },
    {
      slug: 'tendances-ia-juridique-2026',
      title: 'Les 5 tendances IA juridique qui vont transformer 2026',
      excerpt: 'Agents autonomes, IA souveraine, commoditisation… Les tendances qui redessinent le marché.',
      date: '12 septembre 2025',
      category: 'strategic',
      categoryLabel: 'Analyse Stratégique',
      readTime: '20 min',
    },
    {
      slug: 'doctrine-gouvernement-partenariat',
      title: 'Doctrine × Gouvernement : le partenariat qui change tout',
      excerpt: 'Analyse exclusive du partenariat stratégique entre Doctrine et les services juridiques de l\'État.',
      date: '5 septembre 2025',
      category: 'ai-news',
      categoryLabel: 'AI News',
      readTime: '15 min',
    },
    {
      slug: 'etapes-projet-ia-cabinet',
      title: 'Les 8 étapes d\'un projet IA réussi en cabinet d\'avocats',
      excerpt: 'De l\'audit initial au ROI : la méthodologie complète pour déployer l\'IA en cabinet.',
      date: '29 août 2025',
      category: 'diy',
      categoryLabel: 'Do It Yourself',
      readTime: '20 min',
    },
    {
      slug: 'recherche-juridique-ia',
      title: 'Guide pratique : maîtriser la recherche juridique avec l\'IA',
      excerpt: 'Techniques avancées pour exploiter l\'IA dans vos recherches jurisprudentielles et documentaires.',
      date: '22 août 2025',
      category: 'diy',
      categoryLabel: 'Do It Yourself',
      readTime: '15 min',
    },
    {
      slug: 'analyse-contrat-ia',
      title: 'Guide pratique : analyser vos contrats avec l\'IA',
      excerpt: 'Méthodologies éprouvées pour la revue contractuelle et la due diligence assistée par IA.',
      date: '14 août 2025',
      category: 'diy',
      categoryLabel: 'Do It Yourself',
      readTime: '18 min',
    },
    {
      slug: 'redaction-contrat-ia',
      title: 'Guide pratique : rédiger vos contrats avec l\'IA',
      excerpt: 'Méthodologies avancées pour la génération de clauses et l\'optimisation du drafting.',
      date: '8 août 2025',
      category: 'diy',
      categoryLabel: 'Do It Yourself',
      readTime: '20 min',
    },
    {
      slug: 'test-gemini',
      title: 'Nous avons testé Gemini 2.5 pour vous',
      excerpt: 'Le nouveau modèle de Google à l\'épreuve du droit. Analyse de ses capacités pour la recherche juridique.',
      date: '1 août 2025',
      category: 'ai-news',
      categoryLabel: 'AI News',
      readTime: '8 min',
    },
    {
      slug: 'test-harvey',
      title: 'Nous avons testé Harvey pour vous',
      excerpt: 'Analyse approfondie de l\'IA juridique qui équipe les plus grands cabinets mondiaux du Magic Circle.',
      date: '25 juillet 2025',
      category: 'ai-news',
      categoryLabel: 'AI News',
      readTime: '12 min',
    },
    {
      slug: 'test-jimini',
      title: 'Nous avons testé Jimini AI pour vous',
      excerpt: 'L\'IA juridique française qui comprend vraiment le droit français. Notre coup de cœur.',
      date: '18 juillet 2025',
      category: 'ai-news',
      categoryLabel: 'AI News',
      readTime: '10 min',
      isFavorite: true,
    },
    {
      slug: 'test-lexis-ai',
      title: 'Nous avons testé Lexis AI+ pour vous',
      excerpt: 'L\'IA générative intégrée à l\'écosystème LexisNexis. Sources fiables et traçabilité garantie.',
      date: '11 juillet 2025',
      category: 'ai-news',
      categoryLabel: 'AI News',
      readTime: '10 min',
    },
    {
      slug: 'test-doctrine',
      title: 'Nous avons testé Doctrine pour vous',
      excerpt: 'La référence française de la recherche juridique intelligente. Exhaustivité et recherche sémantique.',
      date: '4 juillet 2025',
      category: 'ai-news',
      categoryLabel: 'AI News',
      readTime: '10 min',
    },
    {
      slug: 'eu-ai-act-avocats',
      title: 'L\'EU AI Act : ce que les avocats doivent savoir',
      excerpt: 'Analyse complète du règlement européen sur l\'intelligence artificielle et ses implications pour la profession.',
      date: '27 juin 2025',
      category: 'strategic',
      categoryLabel: 'Analyse Stratégique',
      readTime: '12 min',
    },
    {
      slug: 'impact-ia-avocats',
      title: 'L\'impact de l\'IA sur le métier d\'avocat',
      excerpt: 'Une analyse équilibrée des transformations en cours et des perspectives d\'avenir pour la profession.',
      date: '20 juin 2025',
      category: 'strategic',
      categoryLabel: 'Analyse Stratégique',
      readTime: '15 min',
    },
    {
      slug: 'chatgpt-recherche-juridique',
      title: 'ChatGPT pour la recherche juridique : guide complet',
      excerpt: 'Comment utiliser efficacement ChatGPT pour optimiser vos recherches juridiques.',
      date: '13 juin 2025',
      category: 'diy',
      categoryLabel: 'Do It Yourself',
      readTime: '12 min',
    },
  ] : [
    {
      slug: 'cnil-has-ai-healthcare-guide-regulated-professions',
      title: 'CNIL-HAS AI Healthcare Guide: The Regulator\'s Maximum Standards for All Regulated Professions',
      excerpt: 'Complete analysis of the HAS-CNIL guide. 12 fact sheets, 3 requirement levels, and striking parallels with attorney-client privilege.',
      date: 'March 12, 2026',
      category: 'strategic',
      categoryLabel: 'Strategic Analysis',
      readTime: '18 min',
    },
    {
      slug: 'eu-ai-code-of-practice-content-marking-2026',
      title: 'EU AI Code of Practice: Content Marking Rules That Transform Regulated Professions\' Obligations',
      excerpt: 'Analysis of the 2nd draft Code of Practice (March 2026). Provider/deployer obligations, standardized AI icon, editorial exception for lawyers.',
      date: 'March 12, 2026',
      category: 'strategic',
      categoryLabel: 'Strategic Analysis',
      readTime: '16 min',
    },
    {
      slug: 'legal-ops-career-ai-guide',
      title: 'LegalOps: 5 Things to Know About This Emerging Profession',
      excerpt: 'Origins, missions, AFJE training, salaries €35-70k, and the catalytic role of AI LegalOps in French legal departments.',
      date: 'March 5, 2026',
      category: 'strategic',
      categoryLabel: 'Strategic Analysis',
      readTime: '15 min',
      image: '/images/legal-ops-metier-ia-juridique-guide-complet.webp',
    },
    {
      slug: 'law-firm-automation-ai',
      title: 'Law Firm Automation: 3 Processes to Transform Today',
      excerpt: '40% of your admin workload eliminated. Budget: $45/month. Step-by-step guide.',
      date: 'March 4, 2026',
      category: 'diy',
      categoryLabel: 'Do It Yourself',
      readTime: '12 min',
      image: '/images/automatisation-cabinet-avocat-ia-guide-complet.webp',
    },
    {
      slug: 'tribune-generative-ai-legal-regulatory-inflation',
      title: '[Op-Ed] Generative AI, an Ally for Lawyers Against Regulatory Inflation?',
      excerpt: 'Published on Village de la Justice. 92,424 legislative articles, 248,343 regulatory ones: when AI becomes indispensable for navigating French law.',
      date: 'March 2, 2026',
      category: 'strategic',
      categoryLabel: 'Strategic Analysis',
      readTime: '8 min',
      image: '/images/seo-about-legal-ai-expertise.webp',
    },
    {
      slug: 'ai-hallucinations-french-courts',
      title: 'AI Hallucinations in French Courts: First Cases and Ethical Implications for Lawyers',
      excerpt: 'Analysis of the first French administrative court decisions identifying AI-generated fictitious case law and lawyers\' deontological obligations.',
      date: 'February 26, 2026',
      category: 'strategic',
      categoryLabel: 'Strategic Analysis',
      readTime: '18 min',
    },
    {
      slug: 'ai-agents-lawyers-legal-departments',
      title: 'AI Agents for Lawyers: Understanding the Next Legal Tech Revolution',
      excerpt: 'Clear definition, how they differ from chatbots, and nine concrete use cases for law firms and corporate legal departments.',
      date: 'February 24, 2026',
      category: 'strategic',
      categoryLabel: 'Strategic Analysis',
      readTime: '22 min',
    },
    {
      slug: 'mandatory-cle-french-lawyers',
      title: 'Mandatory CLE for French Lawyers: Complete Guide & the Role of AI',
      excerpt: '20 hours per year, enhanced requirements for young lawyers, e-learning, Paris Bar portal.',
      date: 'February 24, 2026',
      category: 'strategic',
      categoryLabel: 'Strategic Analysis',
      readTime: '18 min',
    },
    {
      slug: 'jimini-ai-gide-partnership',
      title: 'Jimini AI × Gide Loyrette Nouel: The Partnership Shaping French Legal Tech',
      excerpt: 'France\'s largest business law firm selects a French legal AI startup.',
      date: 'February 18, 2026',
      category: 'ai-news',
      categoryLabel: 'AI News',
      readTime: '8 min',
    },
    {
      slug: 'french-bars-legal-tech-partnerships',
      title: 'French Bar Associations & Legal Tech AI: All Partnerships in 2026',
      excerpt: 'Complete mapping of partnerships between French bars and legal AI companies.',
      date: 'February 12, 2026',
      category: 'strategic',
      categoryLabel: 'Strategic Analysis',
      readTime: '15 min',
    },
    {
      slug: 'hoffman-batonnier-ai-legacy',
      title: 'Bâtonnier Pierre Hoffman\'s AI Legacy at the Paris Bar',
      excerpt: '5 legal tech partnerships, 3,000 trained lawyers, a permanent Innovation Committee.',
      date: 'February 5, 2026',
      category: 'strategic',
      categoryLabel: 'Strategic Analysis',
      readTime: '14 min',
    },
    {
      slug: 'ai-budget-companies-vs-law-firms',
      title: '98% of Companies Increase AI Budgets: What About Law Firms?',
      excerpt: 'ABBYY 2025 study: 98% of French companies increase AI budgets. Analysis of the gap with law firms.',
      date: 'January 29, 2026',
      category: 'strategic',
      categoryLabel: 'Strategic Analysis',
      readTime: '16 min',
    },
    {
      slug: 'france-ai-budget-government-2026',
      title: 'France AI Budget 2026: Protected, Preserved or Threatened?',
      excerpt: 'France 2030 cuts, Osez l\'IA plan: analysis of government AI policy and its impact on the legal sector.',
      date: 'January 23, 2026',
      category: 'strategic',
      categoryLabel: 'Strategic Analysis',
      readTime: '18 min',
    },
    {
      slug: 'how-many-ai-tools-lawyer',
      title: 'How Many AI Tools Must a Lawyer Master?',
      excerpt: 'The proliferation of specialized tools demands multidisciplinary training. Ecosystem analysis.',
      date: 'January 16, 2026',
      category: 'strategic',
      categoryLabel: 'Strategic Analysis',
      readTime: '18 min',
    },
    {
      slug: 'webinar-vs-bootcamp-ai-training',
      title: 'Webinar or AI Bootcamp: Which Format?',
      excerpt: 'Detailed comparison between webinars and immersive bootcamps for legal AI upskilling.',
      date: 'January 9, 2026',
      category: 'strategic',
      categoryLabel: 'Strategic Analysis',
      readTime: '16 min',
    },
    {
      slug: 'ai-training-in-house-counsel',
      title: 'AI Training for In-House Counsel: Legal Privilege Challenges',
      excerpt: 'In-house counsel must master AI without compromising confidentiality. Complete guide.',
      date: 'January 2, 2026',
      category: 'strategic',
      categoryLabel: 'Strategic Analysis',
      readTime: '18 min',
    },
    {
      slug: 'legal-ai-training-funding',
      title: 'How to Fund Your Legal AI Training',
      excerpt: 'Complete guide to funding: CPF, mandatory CLE, OPCO, FNE.',
      date: 'December 19, 2025',
      category: 'diy',
      categoryLabel: 'Do It Yourself',
      readTime: '14 min',
    },
    {
      slug: 'francenum-guide-generative-ai-lawyers',
      title: 'France Num Guide on Generative AI for Lawyers',
      excerpt: 'Analysis of the francenum.gouv.fr / CNB practical guide. Strengths, limitations, and international comparison.',
      date: 'December 12, 2025',
      category: 'strategic',
      categoryLabel: 'Strategic Analysis',
      readTime: '15 min',
    },
    {
      slug: 'legal-ai-training-lamy-liaisons-review',
      title: 'Legal AI Training: Lamy Liaisons Académie Review',
      excerpt: 'Detailed program analysis, modules, pricing, strengths and limitations.',
      date: 'December 5, 2025',
      category: 'strategic',
      categoryLabel: 'Strategic Analysis',
      readTime: '18 min',
    },
    {
      slug: 'transfo-droit-2025-legal-ai-congress-recap',
      title: 'TransfoDroit 2025: Complete Recap of Europe\'s Largest Legal AI Congress',
      excerpt: '4,800 attendees, 80 exhibitors, 120+ conferences: detailed recap of the Transformations du Droit congress.',
      date: 'December 2, 2025',
      category: 'strategic',
      categoryLabel: 'Strategic Analysis',
      readTime: '22 min',
    },
    {
      slug: 'genial-dalloz-legal-ai-training-review',
      title: 'GenIA-L & Lefebvre Dalloz AI Training: Complete Analysis',
      excerpt: 'Detailed review of GenIA-L legal AI tool and Lefebvre Dalloz training catalog.',
      date: 'November 28, 2025',
      category: 'strategic',
      categoryLabel: 'Strategic Analysis',
      readTime: '20 min',
    },
    {
      slug: 'best-ai-training-lawyers-comparison',
      title: 'Top 5 AI Training Programs for Lawyers in 2026',
      excerpt: 'Lamy, LexisNexis, EFB, Avocat 2.0, Gaius: which training to master legal AI?',
      date: 'November 21, 2025',
      category: 'strategic',
      categoryLabel: 'Strategic Analysis',
      readTime: '20 min',
    },
    {
      slug: 'cnb-guide-generative-ai-lawyers',
      title: 'CNB Guide on Generative AI for Lawyers',
      excerpt: 'Complete analysis of the first official guide on generative AI by lawyers in France.',
      date: 'November 14, 2025',
      category: 'strategic',
      categoryLabel: 'Strategic Analysis',
      readTime: '18 min',
    },
    {
      slug: 'claude-cowork-legal',
      title: 'Claude Cowork Legal: The Plugin That Shook $285B Off the Markets',
      excerpt: 'Thomson Reuters down 16%. Was the panic justified?',
      date: 'November 7, 2025',
      category: 'ai-news',
      categoryLabel: 'AI News',
      readTime: '22 min',
    },
    {
      slug: 'claude-legal-plugin-review',
      title: 'Claude Legal Plugin 2026: Complete Review',
      excerpt: '3 weeks of intensive testing. The unfiltered verdict.',
      date: 'October 31, 2025',
      category: 'ai-news',
      categoryLabel: 'AI News',
      readTime: '20 min',
    },
    {
      slug: 'gdpr-checklist-legal-ai',
      title: 'The 12-Point GDPR Checklist Before Adopting Legal AI',
      excerpt: 'GDPR and ethical compliance checklist. Hosting, encryption, certifications, AI Act.',
      date: 'October 24, 2025',
      category: 'diy',
      categoryLabel: 'Do It Yourself',
      readTime: '15 min',
    },
    {
      slug: 'ultimate-guide-legal-ai',
      title: 'The Ultimate Guide to Legal AI for Lawyers in 2026',
      excerpt: 'From ChatGPT to Harvey AI: the complete guide to integrating AI.',
      date: 'October 17, 2025',
      category: 'diy',
      categoryLabel: 'Do It Yourself',
      readTime: '25 min',
    },
    {
      slug: 'choose-legal-ai-5-steps',
      title: 'How to Choose Your Legal AI in 5 Steps',
      excerpt: 'The 5-step decision framework to select the right AI tool.',
      date: 'October 10, 2025',
      category: 'diy',
      categoryLabel: 'Do It Yourself',
      readTime: '18 min',
    },
    {
      slug: 'harvey-doctrine-claude-comparison',
      title: 'Harvey AI vs Doctrine vs Claude Legal: 2026 Comparison',
      excerpt: 'Detailed comparison of the 3 dominant legal AI tools.',
      date: 'October 3, 2025',
      category: 'ai-news',
      categoryLabel: 'AI News',
      readTime: '22 min',
    },
    {
      slug: '7-fatal-ai-deployment-mistakes',
      title: 'The 7 Fatal Mistakes in Law Firm AI Deployment',
      excerpt: '87% of AI projects fail in law firms. Here are the 7 mistakes that kill them.',
      date: 'September 26, 2025',
      category: 'strategic',
      categoryLabel: 'Strategic Analysis',
      readTime: '15 min',
    },
    {
      slug: 'best-practices-ai-law-firm',
      title: '10 Best Practices for Integrating AI in Law Firms',
      excerpt: 'The complete playbook from 50+ successful deployments.',
      date: 'September 19, 2025',
      category: 'diy',
      categoryLabel: 'Do It Yourself',
      readTime: '18 min',
    },
    {
      slug: 'legal-ai-trends-2026',
      title: '5 Legal AI Trends That Will Transform 2026',
      excerpt: 'Autonomous agents, sovereign AI, commoditization…',
      date: 'September 12, 2025',
      category: 'strategic',
      categoryLabel: 'Strategic Analysis',
      readTime: '20 min',
    },
    {
      slug: 'doctrine-government-partnership',
      title: 'Doctrine × Government: The Partnership That Changes Everything',
      excerpt: 'Exclusive analysis of the strategic partnership.',
      date: 'September 5, 2025',
      category: 'ai-news',
      categoryLabel: 'AI News',
      readTime: '15 min',
    },
    {
      slug: 'ai-project-steps-law-firm',
      title: 'The 8 Steps to a Successful AI Project in Law Firms',
      excerpt: 'From initial audit to ROI: the complete methodology.',
      date: 'August 29, 2025',
      category: 'diy',
      categoryLabel: 'Do It Yourself',
      readTime: '20 min',
    },
    {
      slug: 'legal-research-ai',
      title: 'Practical Guide: Mastering Legal Research with AI',
      excerpt: 'Advanced techniques for case law and documentary research.',
      date: 'August 22, 2025',
      category: 'diy',
      categoryLabel: 'Do It Yourself',
      readTime: '15 min',
    },
    {
      slug: 'contract-analysis-ai',
      title: 'Practical Guide: Analyzing Contracts with AI',
      excerpt: 'Proven methodologies for contract review and due diligence.',
      date: 'August 14, 2025',
      category: 'diy',
      categoryLabel: 'Do It Yourself',
      readTime: '18 min',
    },
    {
      slug: 'contract-drafting-ai',
      title: 'Practical Guide: Drafting Contracts with AI',
      excerpt: 'Advanced methodologies for clause generation and drafting optimization.',
      date: 'August 8, 2025',
      category: 'diy',
      categoryLabel: 'Do It Yourself',
      readTime: '20 min',
    },
    {
      slug: 'test-gemini',
      title: 'We Have Tested Gemini 2.5 for You',
      excerpt: 'Google\'s new model put to the legal test.',
      date: 'August 1, 2025',
      category: 'ai-news',
      categoryLabel: 'AI News',
      readTime: '8 min',
    },
    {
      slug: 'test-harvey',
      title: 'We Have Tested Harvey for You',
      excerpt: 'In-depth analysis of the legal AI used by Magic Circle firms.',
      date: 'July 25, 2025',
      category: 'ai-news',
      categoryLabel: 'AI News',
      readTime: '12 min',
    },
    {
      slug: 'test-jimini',
      title: 'We Have Tested Jimini AI for You',
      excerpt: 'The French legal AI that truly understands French law. Our top pick.',
      date: 'July 18, 2025',
      category: 'ai-news',
      categoryLabel: 'AI News',
      readTime: '10 min',
      isFavorite: true,
    },
    {
      slug: 'test-lexis-ai',
      title: 'We Have Tested Lexis AI+ for You',
      excerpt: 'Generative AI integrated into the LexisNexis ecosystem.',
      date: 'July 11, 2025',
      category: 'ai-news',
      categoryLabel: 'AI News',
      readTime: '10 min',
    },
    {
      slug: 'test-doctrine',
      title: 'We Have Tested Doctrine for You',
      excerpt: 'The French reference for intelligent legal research.',
      date: 'July 4, 2025',
      category: 'ai-news',
      categoryLabel: 'AI News',
      readTime: '10 min',
    },
    {
      slug: 'eu-ai-act-lawyers',
      title: 'The EU AI Act: What Lawyers Need to Know',
      excerpt: 'Comprehensive analysis of the European AI regulation.',
      date: 'June 27, 2025',
      category: 'strategic',
      categoryLabel: 'Strategic Analysis',
      readTime: '12 min',
    },
    {
      slug: 'ai-impact-lawyers',
      title: 'The Impact of AI on the Legal Profession',
      excerpt: 'A balanced analysis of ongoing transformations.',
      date: 'June 20, 2025',
      category: 'strategic',
      categoryLabel: 'Strategic Analysis',
      readTime: '15 min',
    },
    {
      slug: 'chatgpt-legal-research',
      title: 'ChatGPT for Legal Research: Complete Guide',
      excerpt: 'How to effectively use ChatGPT to optimize your legal research.',
      date: 'June 13, 2025',
      category: 'diy',
      categoryLabel: 'Do It Yourself',
      readTime: '12 min',
    },
  ];

  const filteredArticles = activeCategory === 'all'
    ? articles
    : articles.filter(a => a.category === activeCategory);

  // Layout splits
  const heroArticles = filteredArticles.slice(0, 5);
  const newsArticles = filteredArticles.filter(a => a.category === 'ai-news').slice(0, 4);
  const strategicArticles = filteredArticles.filter(a => a.category === 'strategic').slice(0, 6);
  const diyArticles = filteredArticles.filter(a => a.category === 'diy').slice(0, 4);
  const allFiltered = filteredArticles.slice(0);

  // "Trending" = hand-picked popular articles
  const trendingArticles = language === 'fr'
    ? articles.filter(a => ['claude-cowork-juridique', 'harvey-doctrine-claude-comparatif', 'guide-ultime-ia-juridique', 'test-jimini', 'agents-ia-avocats-directions-juridiques'].includes(a.slug)).slice(0, 5)
    : articles.filter(a => ['claude-cowork-legal', 'harvey-doctrine-claude-comparison', 'ultimate-guide-legal-ai', 'test-jimini', 'ai-agents-lawyers-legal-departments'].includes(a.slug)).slice(0, 5);

  const articleCounts = {
    diy: articles.filter(a => a.category === 'diy').length,
    'ai-news': articles.filter(a => a.category === 'ai-news').length,
    strategic: articles.filter(a => a.category === 'strategic').length,
  };

  const seoTitle = language === 'fr'
    ? "Actualités IA Juridique | Gaius - Analyses, Guides et Veille"
    : "Legal AI News | Gaius - Analysis, Guides and Insights";
  const seoDescription = language === 'fr'
    ? "Actualités, guides pratiques et analyses sur l'IA juridique. Veille IA pour avocats, juristes et directions juridiques. IA Avocat, formation IA droit."
    : "News, guides and analysis on legal AI. Latest developments in artificial intelligence for lawyers.";

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        keywords={language === 'fr' ? "blog ia juridique, actualités ia droit, ia avocat, formation ia avocat, guide chatgpt avocat, veille juridique ia" : "legal ai blog, ai law news, chatgpt lawyer guide, ai agents lawyers"}
        canonicalUrl={language === 'fr' ? "https://gaius-tech.com/actualites" : "https://gaius-tech.com/blog"}
        alternateUrlFr="https://gaius-tech.com/actualites"
        alternateUrlEn="https://gaius-tech.com/blog"
      />

      {/* ─── Masthead ─────────────────────────────────────────────── */}
      <header className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center gap-3 mb-2">
            <Newspaper className="w-6 h-6 text-primary" />
            <span className="text-sm font-semibold text-primary tracking-widest uppercase">
              {language === 'fr' ? 'Veille & Décryptage' : 'Intelligence & Analysis'}
            </span>
          </div>
          <h1 className="text-3xl lg:text-5xl font-bold text-foreground mb-2 leading-[1.1]">
            {language === 'fr' ? 'Actualités IA Juridique' : 'Legal AI Insights'}
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl">
            {language === 'fr'
              ? 'Décryptages, tests d\'outils, analyses stratégiques et veille IA pour les professionnels du droit.'
              : 'Expert analysis, tool reviews, strategic insights and AI intelligence for legal professionals.'}
          </p>
        </div>
      </header>

      {/* ─── Sticky filter bar ───────────────────────────────────── */}
      <nav className="sticky top-16 z-30 bg-background/95 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
            {categories.map((cat) => {
              const Icon = cat.icon;
              return (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`whitespace-nowrap inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                    activeCategory === cat.id
                      ? 'bg-primary text-primary-foreground shadow-md'
                      : 'bg-secondary text-muted-foreground hover:bg-secondary/80 hover:text-foreground'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {cat.label}
                  {cat.id !== 'all' && (
                    <span className="opacity-60 text-xs">{articleCounts[cat.id as keyof typeof articleCounts]}</span>
                  )}
                </button>
              );
            })}
            <span className="ml-auto text-xs text-muted-foreground hidden sm:block">
              {filteredArticles.length} {language === 'fr' ? 'articles' : 'articles'}
            </span>
          </div>
        </div>
      </nav>

      {/* ─── Content ─────────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-10">

        {/* === À LA UNE === */}
        {heroArticles.length > 0 && (
          <section className="mb-12">
            <HeroUne articles={heroArticles} language={language} />
          </section>
        )}

        {/* === Two-column: editorial feed + sidebar === */}
        <div className="lg:grid lg:grid-cols-3 lg:gap-10 mb-12">
          {/* Main column */}
          <div className="lg:col-span-2 space-y-10">

            {/* — AI NEWS section — */}
            {(activeCategory === 'all' || activeCategory === 'ai-news') && newsArticles.length > 0 && (
              <section>
                <SectionHeader icon={TrendingUp} title={language === 'fr' ? 'Legal AI News' : 'Legal AI News'} />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {newsArticles.map((a, i) => (
                    <ImageCard key={i} article={a} language={language} />
                  ))}
                </div>
              </section>
            )}

            {/* — DÉCRYPTAGES / ANALYSES STRATÉGIQUES — */}
            {(activeCategory === 'all' || activeCategory === 'strategic') && strategicArticles.length > 0 && (
              <section>
                <SectionHeader icon={Scale} title={language === 'fr' ? 'Décryptages' : 'Deep Dives'} />
                <div className="divide-y divide-border">
                  {strategicArticles.map((a, i) => (
                    <ArticleRow key={i} article={a} language={language} showBorder={false} />
                  ))}
                </div>
              </section>
            )}

            {/* — Engagement — */}
            <ReaderQuestion language={language} />

            {/* — GUIDES PRATIQUES — */}
            {(activeCategory === 'all' || activeCategory === 'diy') && diyArticles.length > 0 && (
              <section>
                <SectionHeader icon={FileText} title={language === 'fr' ? 'Guides Pratiques' : 'Practical Guides'} />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {diyArticles.map((a, i) => (
                    <ImageCard key={i} article={a} language={language} />
                  ))}
                </div>
              </section>
            )}
          </div>

          {/* Sidebar */}
          <aside className="mt-10 lg:mt-0 space-y-6">
            <TrendingSection articles={trendingArticles} language={language} />

            {/* Newsletter */}
            <div className="rounded-xl border border-primary/10 bg-gradient-to-br from-primary/5 to-accent/5 p-5">
              <h3 className="text-sm font-bold text-foreground mb-1">
                📬 {language === 'fr' ? 'Newsletter IA juridique' : 'Legal AI Newsletter'}
              </h3>
              <p className="text-xs text-muted-foreground mb-3">
                {language === 'fr'
                  ? 'Un décryptage par semaine, directement dans votre boîte mail.'
                  : 'One weekly insight, straight to your inbox.'}
              </p>
              <NewsletterForm language={language} />
            </div>

            {/* External resources */}
            <div className="rounded-xl border border-border bg-card p-5">
              <h3 className="text-sm font-bold text-foreground mb-3">
                {language === 'fr' ? 'Pour aller plus loin' : 'Further Reading'}
              </h3>
              <p className="text-xs text-muted-foreground mb-4">
                {language === 'fr'
                  ? 'Retrouvez davantage d\'informations sur l\'actualité juridique et la profession sur ces sites de référence :'
                  : 'Find more legal news on these reference sites:'}
              </p>
              <div className="space-y-2">
                {(language === 'fr' ? [
                  { name: 'Village de la Justice', url: 'https://www.village-justice.com', desc: 'Communauté juridique' },
                  { name: 'Le Monde du Droit', url: 'https://www.lemondedudroit.fr', desc: 'Média juridique' },
                  { name: 'CNB', url: 'https://www.cnb.avocat.fr', desc: 'Conseil National des Barreaux' },
                  { name: 'AFJE', url: 'https://www.afje.org', desc: 'Association Française des Juristes d\'Entreprise' },
                  { name: 'Commission LegalOps & Innovation AFJE', url: 'https://www.afje.org/commission/legal-operations/', desc: 'Legal Operations et innovation juridique' },
                ] : [
                  { name: 'Village de la Justice', url: 'https://www.village-justice.com', desc: 'Legal community' },
                  { name: 'Le Monde du Droit', url: 'https://www.lemondedudroit.fr', desc: 'Legal media' },
                  { name: 'CNB', url: 'https://www.cnb.avocat.fr', desc: 'National Bar Council' },
                  { name: 'CLOC', url: 'https://cloc.org', desc: 'The Leading Peer-Driven Community for Legal Operations' },
                ]).map((site) => (
                  <a key={site.name} href={site.url} target="_blank" rel="noopener noreferrer" className="group flex items-center gap-3 p-3 rounded-lg border border-border hover:border-primary/30 hover:bg-primary/5 transition-all">
                    <ExternalLink className="w-4 h-4 text-primary shrink-0" />
                    <div>
                      <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">{site.name}</span>
                      <p className="text-xs text-muted-foreground">{site.desc}</p>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </aside>
        </div>

        {/* === ALL ARTICLES grid === */}
        {activeCategory !== 'all' && filteredArticles.length > 4 && (
          <>
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px flex-1 bg-border" />
              <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                {language === 'fr' ? 'Tous les articles' : 'All Articles'}
              </span>
              <div className="h-px flex-1 bg-border" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
              {filteredArticles.slice(4).map((a, i) => (
                <ImageCard key={i} article={a} language={language} />
              ))}
            </div>
          </>
        )}

        {activeCategory === 'all' && (
          <>
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px flex-1 bg-border" />
              <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                {language === 'fr' ? 'Plus d\'actualités' : 'More Articles'}
              </span>
              <div className="h-px flex-1 bg-border" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {articles.slice(10).map((a, i) => (
                <ImageCard key={i} article={a} language={language} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
